"use client";

import { componentCls } from "@faire/slate/lib/tracking";
import { SpinnerBase } from "@faire/slate/private/loading-spinner/SpinnerBase";
import {
  SpinnerSize,
  ISpinnerWebProps,
} from "@faire/slate/private/loading-spinner/type";
import {
  getSpinnerSize,
  getSpinnerStrokeWidth,
} from "@faire/slate/private/loading-spinner/utils";
import { useStrictLocalization } from "@faire/web/common/localization";
import React from "react";

type IProps = {
  /**
   * The color of the spinner.
   *
   * @default slate-icon-default
   */
  color?: string;

  /**
   * The size of the spinner.
   * - `small`: 16px
   * - `medium`: 24px
   * - `large`: 32px
   * - `xLarge`: 48px
   *
   * @default small
   */
  size?: SpinnerSize;

  // DO NOT ADD any new prop without aligning with Slate team first
} & ISpinnerWebProps;

/**
 * A loading spinner represents an unspecified loading time.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * @trackcomponent
 * @version 2.0.0
 */
export const LoadingSpinner: React.FC<IProps> = ({ size, ...props }) => {
  const { strictLocalize } = useStrictLocalization();
  const spinnerSize = getSpinnerSize(size);
  const strokeWidth = getSpinnerStrokeWidth(size);
  return (
    <SpinnerBase
      {...props}
      aria-label={strictLocalize({
        id: "LJ8sQp",
        defaultMessage: "Loading",
        description: {
          text: "Message for screen readers when buttons just show a loading spinner with no text",
        },
      })}
      className={componentCls()}
      role="status"
      size={spinnerSize}
      strokeWidth={strokeWidth}
    />
  );
};
